import { select } from "@angular-redux/store";
import { Component, Input, OnInit,OnDestroy,ViewChild } from "@angular/core";
import { Observable,Subject} from "rxjs";
import { takeUntil} from "rxjs/operators";
import { MsalService } from "../../../msal";
import { PlanningBlock } from "../../../pb-screen/models/planning-block/planning-block.interface";
import { PoolCarrier } from "../../../pool/models/pool-carrier.interface";
import { PoolEquipmentType } from "../../../pool/models/pool-equipment-type.interface";
import { PoolService } from "../../../pool/pool.service";
import { LocationSelectorComponent } from "../../components/location-selector/location-selector.component";
import { AddressLocation } from "../../models/address-location";
import { ComponentSource, ModalType } from "../../models/component-source";
import { FileAddress } from "../../models/file/file-address.interface";
import { File } from "../../models/file/file.interface";
import { UpdateFile } from "../../models/file/update-file.interface";
import { HttpClientService } from "../../services/httpclient";
import { ModalManagerService } from "../../services/modal-manager.service";
import { SharedService } from "../../services/shared.service";
import { SharedActions } from "../../shared.actions";
import { UserFeedbackService } from "../../../services/user-feedback.service";


@Component({
  selector: "dossier-modal",
  templateUrl: "dossier-modal.component.html",
})

export class DossierModalComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  public loading: boolean = false;
  @Input() public source: ComponentSource;
  @ViewChild(LocationSelectorComponent) private locationSelector: LocationSelectorComponent;

  @select("depotOutSelection") private depotOutSelection$: Observable<PlanningBlock[]>;
  @select("depotInSelection") private depotInSelection$: Observable<PlanningBlock[]>;
  @select("loading") public loading$: Observable<boolean>;

  public fileNumber: string;
  public file: File;
  public closingDate: string;
  public depotOutRef1: string;
  public depotInRef1: string;
  public selectedCarrier: PoolCarrier;
  public carriers: PoolCarrier[];
  public selectedContainerType: PoolEquipmentType;
  public selectedPlanningBlocks: PlanningBlock[];
  public containerTypes: PoolEquipmentType[] = [];
  public fileAddresses: FileAddress[] = [];
  public depotIn: FileAddress;
  public depotOut: FileAddress;
  public selectedLocation: AddressLocation = null;

  public closingDateCheck: boolean = true;
  public depotOutCheck: boolean = true;
  public depotInCheck: boolean = true;
  public selectedCarrierCheck: boolean = true;
  public selectedContainerTypeCheck: boolean = true;
  public updateButtonState: boolean = true;
  public locationChanged: boolean = false;
  public modalType = ModalType;

  constructor(
    private readonly sharedService: SharedService,
    private readonly userFeedbackService: UserFeedbackService,
    private readonly sharedActions: SharedActions,
    private readonly poolService: PoolService,
    private readonly modalManager: ModalManagerService,
    private readonly authService: MsalService,
    public readonly httpClientService: HttpClientService,
  ) {
  }

  public ngOnInit(): void {
    this.loading$.subscribe((data) => {
      if (!data) {
        this.loading = data;
      }
    });
    this.httpClientService.getToken().subscribe(_ => {
      this.sharedService.getCarriers().subscribe((carriers: PoolCarrier[]) => {
        this.carriers = carriers;
        this.sharedService.getFileAddresses().subscribe((fileAddresses: FileAddress[]) => {
          this.fileAddresses = fileAddresses;

          this.poolService.getEquipmentTypes().subscribe((poolEquipmentTypes: PoolEquipmentType[]) => {
            this.containerTypes = poolEquipmentTypes;
            this.loadDefaultDossier();
          });
        });
      });
    });
  }

  public loadDefaultDossier(): void {
    if (this.source === ComponentSource.depotIn) {
      this.subscribeToDepotInSelection();
    } else if (this.source === ComponentSource.depotOut) {
      this.subscribeToDepotOutSelection();
    }
  }

  public getSelectedLocation(location: AddressLocation) {
    if (location) {
      if (!this.locationChanged) {
        this.locationChanged = true;
        this.userFeedbackService.showWarningMessage("LOCATION_CHANGE_APPLIED_TO_ALL_ORDERS_IN_THE_FILE");
      }
      this.selectedLocation = location;
    }
  }

  public subscribeToDepotInSelection(): void {
    this.depotInSelection$.pipe(
      takeUntil(this.destroy$)
    ).subscribe((selectedDepotInPlanningBlocks: PlanningBlock[]) => {
      this.selectedPlanningBlocks = selectedDepotInPlanningBlocks;
      this.setDefaultValuesFromSelection(selectedDepotInPlanningBlocks);
    });
  }

  public subscribeToDepotOutSelection(): void {
    this.depotOutSelection$.pipe(
      takeUntil(this.destroy$)
    ).subscribe((selectedDepotOutPlanningBlocks: PlanningBlock[]) => {
      this.selectedPlanningBlocks = selectedDepotOutPlanningBlocks;
      this.setDefaultValuesFromSelection(selectedDepotOutPlanningBlocks);
    });
  }

  public setDefaultValuesFromSelection(selectedPlanningBlocks: PlanningBlock[]): void {
    if (selectedPlanningBlocks && selectedPlanningBlocks.length > 0) {
      this.fileNumber = selectedPlanningBlocks[0].fileId;
      this.setFileFieldsByFileNumber();
    }
  }

  public setFileFieldsByFileNumber(): void {
    this.locationChanged = false;
    if (this.fileNumber) {
      this.sharedService.getFile(this.fileNumber).subscribe((file: File) => {
        if (file) {
          this.file = file;
          this.closingDate = file.closingDateTime;
          this.depotOutRef1 = file.depotOutRef1;
          this.depotInRef1 = file.depotInRef1;

          const depotIn = this.fileAddresses.find((fileAddress: FileAddress) => {
            return fileAddress.addressNo === file.depotIn;
          });
          this.depotIn = depotIn;

          const depotOut = this.fileAddresses.find((fileAddress: FileAddress) => {
            return fileAddress.addressNo === file.depotOut;
          });
          this.depotOut = depotOut;

          const containerType = this.containerTypes.find((poolEquipmentType: PoolEquipmentType) => {
            return poolEquipmentType.code === file.containerType;
          });
          this.selectedContainerType = containerType ? containerType : null;

          const carrier = this.carriers.find((poolCarrier: PoolCarrier) => {
            return poolCarrier.no === file.carrierNo;
          });
          this.selectedCarrier = carrier ? carrier : null;
          this.setFieldsChecks();
        } else {
          this.userFeedbackService.showErrorMessage("File not found");
        }
      }
      );
    }
  }

  public setFieldsChecks(): void {
    this.closingDateCheck = this.closingDate != null && this.closingDate !== "" && this.closingDate !== undefined && this.closingDate.toString() !== "";
    this.depotOutCheck = this.depotOut != null && this.depotOut !== undefined && this.depotOut.toString() !== "";
    this.depotInCheck = this.depotIn != null && this.depotIn !== undefined && this.depotIn.toString() !== "";
    this.selectedCarrierCheck = this.selectedCarrier != null && this.selectedCarrier !== undefined && this.selectedCarrier.toString() !== "";
    this.selectedContainerTypeCheck = this.selectedContainerType != null && this.selectedContainerType !== undefined && this.selectedContainerType.toString() !== "";
    this.setUpdateButtonState();
  }

  public setUpdateButtonState(): void {
    this.updateButtonState = this.closingDateCheck && this.depotOutCheck && this.depotInCheck && this.selectedCarrierCheck && this.selectedContainerTypeCheck;
  }

  public updateDossier(): void {
    const account = this.authService.getAccount();

    this.loading = true;
    if (account.username) {
      this.updateDossierServiceCall(account.username);
    } else {
      this.setDefaultValuesFromSelection(this.selectedPlanningBlocks);
      this.loading = false;
      this.userFeedbackService.showErrorMessage("USER_NOT_FOUND");
    }
  }

  public updateDossierServiceCall(username: string): void {
    const updateDossierModel: UpdateFile = {
      depotOutRef1: this.depotOutRef1,
      depotInRef1: this.depotInRef1,
      closingDate: this.closingDate,
      selectedContainerType: this.selectedContainerType ? this.selectedContainerType.code : "",
      source: this.source,
      userId: username,
      selectedCarrier: this.selectedCarrier ? this.selectedCarrier.no : "",
      depotIn: this.depotIn ? this.depotIn.addressNo : "",
      depotOut: this.depotOut ? this.depotOut.addressNo : " ",
      addressId: (this.selectedLocation && this.selectedLocation.addressId !== this.file.addressNo && this.selectedLocation.locationCode !== this.file.locationCode)
        ? this.selectedLocation.addressId : "",
      locationCode: (this.selectedLocation && this.selectedLocation.addressId !== this.file.addressNo && this.selectedLocation.locationCode !== this.file.locationCode)
        ? this.selectedLocation.locationCode : "",
    };

    this.sharedService.updateFile(this.fileNumber, updateDossierModel)
      .subscribe((planningBlocks: PlanningBlock[]) => {
        this.sharedActions.updatePlanningBlocksDisplayCpl(planningBlocks);
        this.loading = false;
        this.userFeedbackService.showSuccessMessage("TOASTR_MESSAGES.SUCCESS.DOSSIER_UPDATE_SUCCEEDED");
        this.loading = false;
        this.close();
      }, _ => {
        this.loading = false;
      });
  }
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public close(): void {
    this.setFileFieldsByFileNumber();
    this.sharedActions.setUiState(true);
    this.modalManager.closeModal(this.modalType.dossier);
  }
}
